import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import {
  Box,
  CircularProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { usePaymentHistory } from "./hooks/usePaymentHistory";
import { TransactionItem } from "./TransactionItem";

const useStyles = makeStyles((theme) => ({
  modal: {
    borderRadius: theme.spacing(3),
  },
  warningMessage: {
    color: theme.palette.grey[600],
    fontWeight: 400,
    marginLeft: theme.spacing(0.5),
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
  tableHeader: {
    fontWeight: 700,
    fontSize: 12,
    color: theme.palette.grey[600],
    fontFamily: theme.typography.fontFamily,
    textTransform: "uppercase",
  },
}));

export const PaymentHistoryModal: FC<{
  invoiceId: string;
  open: boolean;
  onClose: () => void;
  currency: string;
}> = ({ invoiceId, open, onClose, currency }) => {
  const classes = useStyles();
  const { paymentTransactions, loading } = usePaymentHistory(invoiceId);

  return (
    <DialogContainer open={open} className={classes.modal}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={warningIcon}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.paymentHistory.title"
              defaultMessage="Payment History"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <DialogBody>
        {loading ? (
          <Box width="100%" textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>
                    <FormattedMessage
                      id="invoicing.paymentHistory.header.cashier"
                      defaultMessage="Cashier"
                    />
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    <FormattedMessage
                      id="invoicing.paymentHistory.header.date"
                      defaultMessage="Date & time"
                    />
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    <FormattedMessage
                      id="invoicing.paymentHistory.header.amount"
                      defaultMessage="Amount ({currency})"
                      values={{ currency }}
                    />
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    <FormattedMessage
                      id="invoicing.paymentHistory.header.transactionType"
                      defaultMessage="Transaction"
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentTransactions?.map(
                  ({
                    id,
                    cashier,
                    date,
                    amount,
                    transactionType,
                    paymentMethod,
                    currencyExchange,
                  }) => {
                    if (!cashier || !date || !amount || !transactionType)
                      return null;
                    return (
                      <TransactionItem
                        key={id}
                        cashier={cashier}
                        date={date}
                        amount={amount}
                        transactionType={transactionType}
                        paymentMethod={paymentMethod}
                        currencyExchange={currencyExchange}
                      />
                    );
                  },
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogBody>
      <DialogFooter>
        <Button color="primary" onClick={() => onClose()}>
          <FormattedMessage
            id="invoicing.paymentHistory.close"
            defaultMessage="Close"
          />
        </Button>
      </DialogFooter>
    </DialogContainer>
  );
};
